import {
  BlankItem,
  BlankResponse,
  SelectionOption,
  SorterCategory
} from '../models/LessonComponentContent';
import {
  FillBlankComponent,
  LessonComponentBase,
  LessonComponentResponseType,
  SorterComponent
} from '../models/LessonComponent';
import { isEmpty, isEqual, isNil } from 'lodash';

import LessonComponentService from './LessonComponentService';
import { LessonComponentType } from '../models/LessonComponentType';

export interface SorterAnswer {
  categoryId: string;
  itemId: string;
}

class LessonResponseService {
  /**
   * Returns the string reperesentation of the component-specific user answer
   * that gets stored in the database.
   * @param type Component type.
   * @param value Component-specific response.
   */
  public getResponsePattern(
    type: LessonComponentType,
    value: LessonComponentResponseType
  ): string {
    if (isEmpty(value)) {
      return null;
    }
    switch (type) {
      case 'multiple_selection': {
        const valueType = value as string[];
        return valueType.map((s) => `${s}`).join(`[,]`);
      }
      case 'sorter':
        return this.getSorterResponsePattern(value as SorterCategory[]);
      case 'sequencer':
      case 'multiple_selection_card':
        return JSON.stringify(value);
      default:
        return value as string;
    }
  }

  /**
   * Converts the user response from the database into component-specific
   * response.
   * @param type Component type.
   * @param component Component information; in particular, `component.answer`.
   */
  public getResponseValue(
    type: LessonComponentType,
    component: LessonComponentBase
  ): LessonComponentResponseType {
    const responsePattern = component.answer;
    if (isEmpty(responsePattern)) {
      return null;
    }
    switch (type) {
      case 'single_selection':
      case 'single_selection_card':
      case 'free_text':
      case 'sequencer':
      case 'slider':
      case 'screen_capture':
      case 'screen_capture_comparison':
        return responsePattern as string;
      case 'multiple_selection':
        const responseArray =
          this.convertMultipleSelectionCorrectResponse(responsePattern);
        return responseArray;
      case 'multiple_selection_card':
        return JSON.parse(responsePattern);
      case 'sorter':
        return this.getSorterCategories(
          (component as SorterComponent).content.categories,
          responsePattern
        );
    }
  }

  public isResponseCorrect(
    component: LessonComponentBase,
    correctResponsePattern: string,
    responsePattern: string,
    options: SelectionOption[]
  ): boolean | null {
    //* 3-states: null(not answered) | false | true
    try {
      if (!responsePattern) {
        return null;
      }
      switch (component.type) {
        case 'single_selection':
        case 'single_selection_card':
        case 'slider':
        case 'sequencer':
          return isEqual(correctResponsePattern, responsePattern);
        case 'multiple_selection_card':
          return isEqual(
            JSON.parse(correctResponsePattern).sort(),
            JSON.parse(responsePattern).sort()
          );
        case 'multiple_selection': {
          const userResponse =
            this.convertMultipleSelectionCorrectResponse(responsePattern);
          const correctResponse = this.convertMultipleSelectionCorrectResponse(
            correctResponsePattern
          ).filter((v) => options.map((o) => o.value).includes(v)); //filter the correct response that are all in the options.
          //This is because the author might delete the option that was selected as the correct response
          return isEqual(userResponse.sort(), correctResponse.sort());
        }
        case 'free_text':
        case 'screen_capture':
        case 'screen_capture_comparison':
          return !isNil(responsePattern);
        case 'video':
        case 'full_width_video':
          return null; //* The video component saves the watch progress as an answer.
        //* There's no correct or wrong responses. It's categorised as an interactive component.
        //* It doesn't become inactive at all, so just return null.
        case 'video_uploader':
          //* There's no correct or wrong responses. It's categorised as an interactive component.
          //* It doesn't become inactive at all, so just return null.
          return null;
        case 'sorter':
          return this.isSorterAnswerCorrect(
            correctResponsePattern,
            responsePattern
          );
        case 'fill_blank':
        case 'fill_menu':
          return this.isBlankAnswersCorrect(
            component as FillBlankComponent,
            responsePattern
          );
      }
    } catch (error) {
      return false;
    }
  }

  public canSaveAnswer(
    component: LessonComponentBase,
    responsePattern: string
  ) {
    switch (component.type) {
      case 'sorter': {
        const sorter = component as SorterComponent;
        const sortedCategories = this.getSorterCategories(
          sorter.content.categories,
          responsePattern
        );

        return (
          this.getSorterItems(sortedCategories).length ===
          this.getSorterItems(sorter.content.categories).length
        );
      }
      default:
        return !!responsePattern;
    }
  }

  public convertMultipleSelectionCorrectResponse(pattern: string) {
    if (!pattern) {
      return [];
    }
    return pattern.split('[,]');
  }

  public getSorterItems(
    categories: SorterCategory[],
    randomize: boolean = true
  ) {
    if (isEmpty(categories)) {
      return [];
    }

    const items = [].concat(...categories.map((c) => [].concat(...c.items)));

    if (randomize) {
      LessonComponentService.randomizeItems(items);
    }

    return items;
  }

  public getSorterResponsePattern(categories: SorterCategory[]): string {
    const categoriesWithItems = categories.filter(
      (category) => !isEmpty(category.items)
    );
    return isEmpty(categoriesWithItems)
      ? null //* Return null if no answers, which LessonPlayer assumes.
      : categoriesWithItems
          .map((category) =>
            category.items
              .map((item) => `${category.id}[.]${item.id}`)
              .join('[,]')
          )
          .join('[,]');
  }

  public getSorterCategories(
    categories: SorterCategory[],
    answer: string
  ): SorterCategory[] {
    if (!answer) {
      return categories.map((category) => ({ ...category, items: [] }));
    }
    const allItems = [].concat(...categories.map((c) => [].concat(...c.items)));
    const convertedCategories: SorterCategory[] = [];
    const categoryItemPairs = answer.split('[,]');
    for (const pair of categoryItemPairs) {
      const [categoryId, itemId] = pair.split('[.]');
      const category = categories.find((c) => c.id === categoryId);
      let newCategory = convertedCategories.find((c) => c.id === categoryId);
      if (!newCategory) {
        newCategory = { ...category, items: [] };
        convertedCategories.push(newCategory);
      }
      const item = allItems.find((i) => i.id === itemId);
      newCategory.items.push(item);
    }
    return convertedCategories;
  }

  public isSorterAnswerCorrect(correctResponsePattern: string, answer: string) {
    //* The answer format is: category1[.]item1[,]category2[.]item2[,]...
    const correctCategoryItemPairs = correctResponsePattern.split('[,]').sort();
    const answerCategoryItemPairs = answer.split('[,]').sort();

    return isEqual(correctCategoryItemPairs, answerCategoryItemPairs);
  }

  private isBlankAnswersCorrect(component: FillBlankComponent, answer: string) {
    const options = component.content.blank;
    const userAnswer: BlankResponse[] = JSON.parse(answer);
    const correct: boolean[] = [];
    userAnswer.map((u) => {
      const result = this.isEachBlankAnswerCorrect(
        options,
        u.id,
        u.answer,
        component.content.correctResponsePattern
      );
      correct.push(result);
    });
    return correct.every((c) => c) && options.length === userAnswer.length;
  }

  public isEachBlankAnswerCorrect(
    options: BlankItem[],
    id: string,
    answer: string,
    correctResponsePattern?: string
  ) {
    let result: boolean = false;
    if (correctResponsePattern) {
      // Menu version of fill blanks.
      const corrects = JSON.parse(correctResponsePattern);
      result = corrects[id] === answer;
    } else {
      //Handle case sensitive
      const actual = options.find((o) => o.id === id);
      if (actual.matchCase) {
        result = actual.options.includes(answer);
      } else {
        result = actual.options.some(
          (a) => a.toLowerCase() === answer.toLowerCase()
        );
      }
    }
    return result;
  }

  public getSorterWrongAnswers(
    categories: SorterCategory[],
    correctResponsePattern: string
  ): SorterAnswer[] {
    //* This is used to put the wrong answers(items) back to the source pile
    //* when the user retries in a 'Basic' lesson.
    const correctAnswers: SorterAnswer[] = correctResponsePattern
      .split('[,]')
      .map((pairString) => {
        const pair = pairString.split('[.]');
        return { categoryId: pair[0], itemId: pair[1] };
      });

    const wrongAnswers: SorterAnswer[] = [];
    for (const category of categories) {
      for (const item of category.items) {
        const correctAnswer = correctAnswers.find(
          (a) => a.categoryId === category.id && a.itemId === item.id
        );
        if (!correctAnswer) {
          wrongAnswers.push({ categoryId: category.id, itemId: item.id });
        }
      }
    }

    return wrongAnswers;
  }

  public checkSorterResults(categories: SorterCategory[], pattern: string) {
    const categoryItemPairs = pattern.split('[,]');
    for (const pair of categoryItemPairs) {
      const [categoryId, itemId] = pair.split('[.]');
      const category = categories.find((c) => c.id === categoryId);
      if (!category.items.find((item) => item.id === itemId)) {
        return false;
      }
    }

    return true;
  }
}

export default new LessonResponseService();
